import React from 'react';
import { useTheme } from '../context/theme';
import { computePhaseProgress } from '../utils/site-constants';

export default function WorkflowPhaseStepper({ wsList, gates, selectedPhases, onPhaseClick }) {
  const { theme, isDark } = useTheme();
  const phases = computePhaseProgress(wsList);
  if (!wsList || wsList.length === 0) return null;

  // Build gate lookup
  const gateMap = {};
  (gates || []).forEach(g => { gateMap[g.phase] = g; });

  // A phase is selectable only if it has workstreams AND the gate is not locked
  const isSelectable = (p) => {
    if (p.status === 'empty') return false;
    const gate = gateMap[p.name];
    if (gate && gate.status === 'locked') return false;
    return true;
  };

  const isSelected = (name) => selectedPhases && selectedPhases.has(name);
  const hasAnySelected = selectedPhases && selectedPhases.size > 0;

  return (
    <div style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: 14, padding: '16px 20px', marginBottom: 16, overflowX: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: 1.5 }}>
          Workflow Progress
        </div>
        <button
          onClick={() => onPhaseClick && onPhaseClick(hasAnySelected ? '__clear__' : '__hide_all__')}
          style={{ fontSize: 11, color: theme.textSecondary, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
        >
          {hasAnySelected ? 'Show All' : 'Hide All'}
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%', position: 'relative' }}>
        {phases.map((p, i) => {
          const isComplete = p.status === 'complete';
          const isActive = p.status === 'in-progress';
          const isBlocked = p.status === 'blocked';
          const selectable = isSelectable(p);
          const sel = isSelected(p.name);

          const dimmed = hasAnySelected && !sel;
          const dotColor = isComplete ? '#4ADE80' : isBlocked ? '#EF4444' : isActive ? '#4ADE80' : (isDark ? '#555' : '#ccc');
          const textColor = dimmed ? (isDark ? '#555' : '#bbb') : isComplete ? '#4ADE80' : isBlocked ? '#EF4444' : isActive ? '#4ADE80' : theme.textSecondary;
          const lineColor = isComplete ? '#4ADE80' : (isDark ? '#444' : '#ddd');

          const selRing = sel ? '0 0 0 3px ' + (isComplete ? 'rgba(22,163,74,0.5)' : isBlocked ? 'rgba(220,38,38,0.5)' : 'rgba(59,130,246,0.5)') : '';
          const glowShadow = isBlocked ? '0 0 8px rgba(220,38,38,0.4)' : isActive ? '0 0 8px rgba(59,130,246,0.4)' : 'none';
          const boxShadow = [selRing, glowShadow].filter(Boolean).join(', ') || 'none';

          return (
            <React.Fragment key={p.name}>
              {/* Phase dot + label */}
              <div
                onClick={() => { if (selectable) onPhaseClick && onPhaseClick(p.name); }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60,
                  cursor: selectable ? 'pointer' : 'not-allowed',
                  opacity: !selectable ? 0.35 : dimmed ? 0.5 : 1,
                  transition: 'opacity 0.3s',
                }}
              >
                <div style={{
                  width: sel ? 28 : isActive ? 24 : 20, height: sel ? 28 : isActive ? 24 : 20, borderRadius: '50%',
                  background: isComplete ? '#4ADE80' : isBlocked ? '#EF4444' : isActive ? '#4ADE80' : sel ? '#4ADE80' : 'transparent',
                  border: '3px solid ' + (sel && !isComplete && !isBlocked && !isActive ? '#4ADE80' : dotColor),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.3s', boxShadow, position: 'relative', zIndex: 2,
                }}>
                  {isComplete && <span style={{ color: '#fff', fontSize: 10, fontWeight: 800, lineHeight: 1 }}>✓</span>}
                  {isBlocked && <span style={{ color: '#fff', fontSize: 10, fontWeight: 800, lineHeight: 1 }}>!</span>}
                  {sel && !isComplete && !isBlocked && <span style={{ color: '#fff', fontSize: 10, fontWeight: 800, lineHeight: 1 }}>●</span>}
                </div>
                <div style={{ fontSize: 12, fontWeight: 400, fontFamily: "'DM Sans', sans-serif", color: sel ? (isDark ? '#fff' : '#111') : textColor, marginTop: 6, textAlign: 'center', lineHeight: 1.2, whiteSpace: 'nowrap', transition: 'color 0.3s' }}>
                  {p.name}
                </div>
                {p.total > 0 && (
                  <div style={{ fontSize: 10, color: textColor, marginTop: 2, fontWeight: 600, opacity: 0.8 }}>
                    {p.complete}/{p.total}
                  </div>
                )}
              </div>
              {/* Connecting line — equal flex between each pair */}
              {i < phases.length - 1 && (
                <div style={{ flex: 1, height: 3, background: lineColor, marginTop: 10, minWidth: 12, borderRadius: 2, alignSelf: 'flex-start', transition: 'background 0.3s' }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
